import { DefaultApi, DataSourceType, TableauCheckConnection200Response } from "@coeff/api";
import { CONFIG_DRIVEN_AI_DATA_SOURCE_TYPES, MULTIPLE_OAUTH_DATASOURCES } from "@coeff/utils";
import * as Sentry from "@sentry/react";
import type { AxiosError as AxiosErrorType } from "axios";
import isAxiosError from "axios";

// Type definitions
type AxiosError<T = any> = AxiosErrorType<T>;

export type AxiosErrorWithReason = AxiosError & {
  response: NonNullable<
    AxiosError["response"] & {
      data: {
        reason: string;
        message?: string;
        details?: string;
        source_error?: string;
        source_error_title?: string;
        title?: string;
      };
    }
  >;
};

export type AxiosErrorWithType = AxiosError & {
  response: NonNullable<
    AxiosError["response"] & {
      data: {
        type?: string;
        message?: string;
      };
    }
  >;
};

export type ErrorWithMessage = {
  message: string;
};

// Type guard functions
export const isAxiosErrorWithReason = (error: unknown): error is AxiosErrorWithReason => {
  if (!isAxiosError.isAxiosError(error)) {
    return false;
  }
  const axiosError = error as AxiosError;
  return (
    axiosError.response !== undefined &&
    axiosError.response.data !== undefined &&
    axiosError.response.data !== null &&
    typeof axiosError.response.data === "object" &&
    "reason" in axiosError.response.data
  );
};

export const isAxiosReauthError = (error: unknown): error is AxiosErrorWithReason => {
  if (!isAxiosErrorWithReason(error)) {
    return false;
  }
  return (
    error.response.data.reason === "data_source_needs_reauth" ||
    error.response.data.reason === "invalid_credentials"
  );
};

export const isTableauCheckConnectionResponse = (
  response: unknown
): response is TableauCheckConnection200Response => {
  return typeof response === "object" && response !== null && response.hasOwnProperty("valid");
};

export const extractTableauSiteNameFromURL = (url: string): string | null => {
  const urlParts = url.match("/site/(.*?)/");
  return urlParts ? urlParts[1] : null;
};

/**
 * Simplified version of getReauthErrorMessageProps for Tableau connector
 * Returns props for ErrorMessage component when handling reauth errors
 */
export const getReauthErrorMessageProps = (params: {
  dataImport?: any;
  dataSource?: any;
  userId?: string;
  action?: (() => void) | undefined;
  importStatusOrReauthError?: any;
}): any => {
  const { dataSource, action, importStatusOrReauthError } = params;

  const commonErrorProps = {
    variant: "warning",
    isDataSourceReauthError: true,
    dataSourceConnectionStatus: dataSource?.connection_status,
    dataImport: params.dataImport,
    dataSourceType: "tableau",
    title: importStatusOrReauthError?.title ?? "Reconnect Tableau",
    ...(importStatusOrReauthError
      ? {
          secondaryTitle: importStatusOrReauthError?.source_error_title,
          secondaryMessage: importStatusOrReauthError?.source_error,
        }
      : {}),
  };

  if (action) {
    return {
      ...commonErrorProps,
      message:
        importStatusOrReauthError?.message ??
        "You need to reconnect your Tableau account to Coefficient",
      actionText: "Edit connection",
      action,
    };
  }

  const ownerEmail = dataSource?.user?.email;

  return {
    ...commonErrorProps,
    message:
      importStatusOrReauthError?.message ??
      `Ask the data source owner ${
        ownerEmail ? `(${ownerEmail})` : ""
      } to reconnect their Tableau account`,
  };
};

// URL utilities
export const getBaseUrl = (url: string) => {
  // strip out everything except base url
  // this helper is used in looker connection form
  const baseUrlMatch = url.match(/^(https?:\/\/[^/]+)/);
  if (baseUrlMatch) {
    return baseUrlMatch[0];
  }

  return url;
};

// Sentry utilities
export const sentryCapture = ({
  context,
  error,
  name,
  screen,
}: {
  context?: { [x: string]: unknown };
  error: unknown;
  name?: string;
  screen?: string;
}) => {
  Sentry.captureException(error, (scope: any) => {
    if (name) {
      scope.setTransactionName(name);
    }
    if (screen) {
      scope.setExtra("screen", screen);
    }
    if (context) {
      scope.setContext("coeffctx", context);
    }
    return scope;
  });
};

/**
 * Creates an OAuth attempt and returns the authorization URL
 * Uses ppressRenderAuthForm API for PPress-based OAuth flows
 */
export const getOAuthAuthorizationUrl = async (
  apiClient: DefaultApi,
  params: {
    email?: string;
    oauthAttemptId?: string;
    dataSourceId?: string;
    dataSourceType: string;
    ppress_params?: Record<string, any>;
    ppress_metadata?: Record<string, any>;
    from?: "addon" | "webapp";
    coeffDevRedirectBaseUrl?: string;
  }
): Promise<string> => {
  // Merge oauth_client_id from metadata into params if needed for Snowflake
  let ppress_params = params.ppress_params;
  if (params.dataSourceType === "snowflake") {
    ppress_params = {
      ...ppress_params,
      client_id: params.ppress_metadata?.["client_id"] ?? ppress_params?.client_id,
      config_domain: params.ppress_metadata?.["config_domain"] ?? ppress_params?.config_domain,
    };
  }

  const res = await apiClient.ppressRenderAuthForm({
    ppress_render_auth_form_request: {
      data_source_type: params.dataSourceType as DataSourceType,
      data_source_id: params.dataSourceId,
      ppress_params,
    },
  });

  const {
    data: { oauth2_authorization_url, oauth_client_id, ppress_params: freshPpressParams },
  } = res;

  if (!oauth2_authorization_url) {
    throw new Error("OAuth authorization URL not returned from server");
  }

  // Build state parameter with metadata
  let ppress_metadata = params.ppress_metadata;
  if (oauth_client_id !== undefined) {
    ppress_metadata = {
      ...(ppress_metadata ?? {}),
      oauth_client_id,
    };
  }

  if (freshPpressParams?.code_challenge) {
    ppress_metadata = {
      ...(ppress_metadata ?? {}),
      code_challenge: freshPpressParams.code_challenge,
    };
  }

  const state = JSON.stringify({
    from: params.from ?? "addon",
    data_source_type: params.dataSourceType,
    oauth_attempt_id: params.oauthAttemptId,
    data_source_id: params.dataSourceId,
    check_if_data_source_changed:
      MULTIPLE_OAUTH_DATASOURCES.has(params.dataSourceType ?? "") ||
      params.dataSourceType === DataSourceType.Snowflake ||
      params.dataSourceType === DataSourceType.Looker,
    email: params.email,
    ppress_metadata,
    ...(params.coeffDevRedirectBaseUrl
      ? {
          coeff_dev_redirect_base_url: params.coeffDevRedirectBaseUrl,
        }
      : {}),
  });

  const url = `${oauth2_authorization_url}&state=${encodeURIComponent(state)}`;
  return url;
};

// PPress types
export type AuthorizePPressParams = {
  postValidationNeeded?: boolean;
  state_token_params?: Record<string, string>;
  ppress_metadata?: Record<string, string>;
  oauth2_authorization_url?: string;
  shop_url?: string;
  account_name?: string;
  account_id?: string;
  gcloud_project_id?: string;
  is_salesforce_sandbox?: string;
  snowflake_account?: string;
  client_id?: string;
  config_domain?: string;
  database_name?: string;
  instance_url?: string;
  jdbc_url?: string;
  catalog_name?: string;
  site_name?: string;
  subdomain?: string;
  snowflake_warehouse_name?: string;
  client_app_guid?: string;
  code_challenge?: string;
  base_url?: string;
  selected_schemas?: string[];
  data_center?: string;
  store_id?: string;
  available_client_ids?: Record<string, string>;
  available_domains?: Record<string, string>;
  available_auth_urls?: Record<string, string>;
  egnyte_domain?: string;
};

/**
 * Builds PPress metadata for tracking
 * Used by connectors that use PPress authentication
 */
export const buildPPressMetadata = ({
  ppress_params = {},
  dataSourceType,
}: {
  ppress_params?: AuthorizePPressParams;
  dataSourceType: DataSourceType | string;
}): Record<string, unknown> => {
  if ((CONFIG_DRIVEN_AI_DATA_SOURCE_TYPES as readonly string[]).includes(dataSourceType)) {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(ppress_params || {})) {
      if (key.startsWith("ai_connector_param_")) {
        result[key.replace("ai_connector_param_", "")] = value;
      }
    }
    return result;
  }

  switch (dataSourceType) {
    case "shopify": {
      return {
        shop_url: ppress_params.shop_url?.endsWith(".myshopify.com")
          ? ppress_params.shop_url
          : ppress_params.shop_url + ".myshopify.com",
      };
    }
    case "egnyte": {
      return {
        egnyte_domain: ppress_params.egnyte_domain,
      };
    }
    case "bigquery": {
      return {
        gcloud_project_id: ppress_params.gcloud_project_id,
      };
    }
    case "salesforce": {
      return {
        is_salesforce_sandbox: ppress_params.is_salesforce_sandbox,
      };
    }
    case "looker": {
      return {
        base_url: ppress_params.base_url,
        client_id: ppress_params.client_id,
        client_app_guid: ppress_params.client_app_guid,
        config_domain: ppress_params.config_domain,
        code_challenge: ppress_params.code_challenge,
      };
    }
    case "tableau": {
      return {
        client_id: ppress_params.client_id,
        config_domain: ppress_params.config_domain,
        instance_url: ppress_params.instance_url,
        site_name: ppress_params.site_name,
      };
    }
    case "databricks": {
      return {
        client_id: ppress_params.client_id,
        config_domain: ppress_params.config_domain,
        jdbc_url: ppress_params.jdbc_url,
        catalog_name: ppress_params.catalog_name,
      };
    }
    case "arrow_flight_sql": {
      return {
        client_id: ppress_params.client_id,
        config_domain: ppress_params.config_domain,
        database_name: ppress_params.database_name,
      };
    }
    // Like arrow_flight_sql, Quiver's OAuth client is registered per-domain in
    // DomainConfigJSON, so the backend needs both the client_id and the domain that
    // owns it to look the app info back up during the token exchange. Falling through
    // to the default {} here would drop them and fail with a KeyError server-side.
    case "netflix_quiver": {
      return {
        client_id: ppress_params.client_id,
        config_domain: ppress_params.config_domain,
      };
    }
    case "snowflake": {
      return {
        snowflake_account: ppress_params.snowflake_account,
        client_id:
          ppress_params.available_client_ids?.[ppress_params.snowflake_account ?? ""] ??
          ppress_params.client_id,
        config_domain:
          ppress_params.available_domains?.[ppress_params.snowflake_account ?? ""] ??
          ppress_params.config_domain,
        database_name: ppress_params.database_name,
        snowflake_warehouse_name: ppress_params.snowflake_warehouse_name,
        selected_schemas: ppress_params.selected_schemas,
      };
    }
    case "campfire": {
      return {};
    }
    case "rillet": {
      return {};
    }
    case "chargebee": {
      return {
        site_name: ppress_params.site_name,
      };
    }
    case "zendesk": {
      const { subdomain } = ppress_params;

      return {
        subdomain: subdomain?.startsWith("https")
          ? subdomain.slice(8, subdomain.indexOf("."))
          : subdomain,
      };
    }
    case "gong": {
      return {
        account_name: ppress_params.account_name,
      };
    }
    case "reddit_ads": {
      return {
        account_id: ppress_params.account_id,
      };
    }
    case "intercom": {
      return {
        data_center: ppress_params.data_center,
      };
    }
    case "basecamp": {
      return {
        account_id: ppress_params.account_id,
      };
    }
    case "bigcommerce": {
      return {
        store_id: ppress_params.store_id,
      };
    }
    case "netsuite": {
      return {
        code_challenge: ppress_params.code_challenge,
      };
    }
    case "aws_billing": {
      return {
        site_name: ppress_params.site_name,
      };
    }
    default: {
      return {};
    }
  }
};

/**
 * Extracts a user-friendly error message from API errors
 * Used across all auth flows for consistent error handling
 */
export const getDataSourceApiErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return "An error occurred. Please try again.";
};

/**
 * Get description text for authorization screens
 * Used primarily by Amplitude connector
 */
export const getAuthorizeDescription = (
  dataSourceType: DataSourceType | undefined,
  dataSourceId: string | undefined
): string => {
  return dataSourceId
    ? `To continue with your import, click to re‑authorize Coefficient to connect to ${
        dataSourceType || ""
      }`
    : `Authorize Coefficient to connect to ${dataSourceType || ""}`;
};

/**
 * Masks password in connection strings for SQL data sources
 * Used by SQL connector for security
 */
export const getMaskedConnectionString = (connectionStr: string | undefined): string => {
  try {
    if (connectionStr === undefined) {
      return "*****";
    }
    if (String(connectionStr).includes("jdbc:databricks")) {
      const passwordToMask = connectionStr.substring(connectionStr.lastIndexOf("PWD=") + 4);
      return connectionStr.split(passwordToMask).join("*****");
    }
    if (!String(connectionStr).includes("mongodb+srv")) {
      return String(connectionStr);
    }
    const usernamePass = String(connectionStr).match(/mongodb\+srv:\/\/([^@]+)/);
    if (!usernamePass || !usernamePass[1]) {
      return String(connectionStr);
    }
    const passwordToMask = usernamePass[1].substring(usernamePass[1].lastIndexOf(":") + 1);
    return connectionStr.split(passwordToMask).join("*****");
  } catch (error) {
    return "*****";
  }
};

/**
 * Check if error message indicates a warehouse connection issue
 * Used by Snowflake connector
 */
export const isWarehouseConnectionError = (errorMessage: string | null): boolean => {
  return !!(
    errorMessage &&
    (errorMessage.includes("No default warehouse") ||
      errorMessage.includes("Confirm warehouse exists") ||
      errorMessage.includes("Could not find any warehouses"))
  );
};

/**
 * Check if error message indicates a Sage whitelist issue
 * Used by Sage connector
 */
export const isSageWhitelistError = (
  dataSourceType: DataSourceType,
  errorMessage: string | null
): boolean => {
  return (
    dataSourceType === "sage" &&
    !!errorMessage &&
    errorMessage.includes("sender ID 'CoefficientMPP'")
  );
};

/**
 * Capitalize first letter of a string
 */
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Case-insensitive string search
 */
export const stringSearch = (search: string, target: string): boolean => {
  return target.toLowerCase().includes(search.toLowerCase());
};

// Stub functions for DataSourceItem compatibility
// These are addon-specific and should be implemented properly if needed
export const enforcePremiumConnectorLogicForDataSource = (dataSource: any) => dataSource;
export const useDataSourceExtraInfo = (dataSource: any) => ({ isLoading: false, data: {} });
export const getUserDisplayName = (user: any) => user?.name || user?.email || "";
export const isAPIKeyDataSourceConnection = (dataSource: any) => false;
export const isDataSourceTypeDisabledByAdmin = (dataSource: any) => false;
export const assertIsDefined: <T>(value: T) => asserts value is NonNullable<T> = <T>(
  value: T
): asserts value is NonNullable<T> => {
  if (value === undefined || value === null) {
    throw new Error("Value is not defined");
  }
};
export const getEditConnectionSettingsRoute = (dataSource: any) => "";
export const getAuthorizationUrl = getOAuthAuthorizationUrl;

// Utility function for pluralizing words
export const pluralize = (word: string, count: number, includeCount = false) => {
  if (count === 1) {
    return includeCount ? `1 ${word}` : word;
  }
  return includeCount ? `${count} ${word}s` : `${word}s`;
};

// Stub for useLocationState - addon-specific utility that depends on react-router-dom
// In the shared library context, this returns default values
// The addon wrapper should provide the actual values via props if needed
export const useLocationState = <T extends object = any>(): T => {
  return {} as T;
};
